import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Users, UserPlus, MapPin, Phone, Mail, Calendar,
  Loader2, Search, ChevronDown, ChevronUp, Edit2,
  Folder, AlertCircle, CheckCircle, XCircle, Plus,
  Heart, FileText, Stethoscope, Shield, Trash2, Activity
} from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700 border-green-200',
  inactive: 'bg-gray-100 text-gray-700 border-gray-200',
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200'
};

export default function ClientManagement() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClient, setExpandedClient] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [loadingAssignments, setLoadingAssignments] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState({});
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    primary_diagnosis: '',
    secondary_diagnoses: '',
    insurance_provider: '',
    insurance_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
    fetchEmployees();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/users');
      // Filter to only caregivers/staff
      const caregivers = response.data.filter(u => 
        ['caregiver', 'dsp', 'lpn', 'rn'].includes(u.role?.toLowerCase())
      );
      setEmployees(caregivers);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchAssignments = async (clientId) => {
    setLoadingAssignments(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await api.get(`/clients/${clientId}/assignments`);
      setAssignments(prev => ({ ...prev, [clientId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      // Table might not exist yet - handle gracefully
      setAssignments(prev => ({ ...prev, [clientId]: [] }));
    } finally {
      setLoadingAssignments(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleExpand = async (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      await fetchAssignments(clientId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessingId('new');
    
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, formData);
        toast.success('Client updated successfully');
      } else {
        await api.post('/clients', formData);
        toast.success(
          <div>
            <p className="font-semibold">Client created successfully!</p>
            <p className="text-sm text-gray-600">Medical chart folder created</p>
          </div>
        );
      }
      
      setShowAddForm(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Failed to save client:', error);
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setProcessingId(null);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      date_of_birth: client.date_of_birth || '',
      gender: client.gender || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      phone: client.phone || '',
      email: client.email || '',
      emergency_contact_name: client.emergency_contact_name || client.primary_contact_name || '',
      emergency_contact_phone: client.emergency_contact_phone || client.primary_contact_phone || '',
      emergency_contact_relationship: client.emergency_contact_relationship || '',
      primary_diagnosis: client.primary_diagnosis || '',
      secondary_diagnoses: client.secondary_diagnoses || '',
      insurance_provider: client.insurance_provider || '',
      insurance_id: client.insurance_id || '',
      notes: client.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDeactivate = async (clientId) => {
    if (!window.confirm('Are you sure you want to deactivate this client?')) return;
    
    setProcessingId(clientId);
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deactivated');
      fetchClients();
    } catch (error) {
      console.error('Failed to deactivate client:', error);
      toast.error('Failed to deactivate client');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAssign = async (clientId) => {
    const employeeId = selectedEmployee[clientId];
    if (!employeeId) {
      toast.error('Please select an employee');
      return;
    }
    
    setProcessingId(`assign-${clientId}`);
    try {
      await api.post(`/clients/${clientId}/assign`, { employee_id: employeeId });
      toast.success('Employee assigned to client');
      setSelectedEmployee(prev => ({ ...prev, [clientId]: '' }));
      fetchAssignments(clientId);
    } catch (error) {
      console.error('Failed to assign employee:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign employee');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUnassign = async (clientId, employeeId) => {
    setProcessingId(`unassign-${clientId}-${employeeId}`);
    try {
      await api.delete(`/clients/${clientId}/assign/${employeeId}`);
      toast.success('Assignment removed');
      fetchAssignments(clientId);
    } catch (error) {
      console.error('Failed to remove assignment:', error);
      toast.error('Failed to remove assignment');
    } finally {
      setProcessingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', date_of_birth: '', gender: '',
      address: '', city: '', state: '', zip_code: '', phone: '', email: '',
      emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
      primary_diagnosis: '', secondary_diagnoses: '', insurance_provider: '', insurance_id: '', notes: ''
    });
  };

  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.first_name?.toLowerCase().includes(query) ||
      client.last_name?.toLowerCase().includes(query) ||
      client.city?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-pink-100 rounded-xl">
            <Heart className="w-8 h-8 text-pink-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Client Management
            </h2>
            <p className="text-gray-600">Manage patients and their care assignments</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-64"
              data-testid="client-search"
            />
          </div>
          <Button
            onClick={() => { setShowAddForm(true); setEditingClient(null); resetForm(); }}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Client
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-purple-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{clients.filter(c => c.status === 'active').length}</p>
              <p className="text-sm text-gray-600">Active Clients</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-purple-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <XCircle className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{clients.filter(c => c.status === 'inactive').length}</p>
              <p className="text-sm text-gray-600">Inactive Clients</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-purple-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
              <p className="text-sm text-gray-600">Total Clients</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-purple-100">
              <h3 className="text-xl font-semibold text-purple-900">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {editingClient ? 'Update client information' : 'Enter client details to create their profile and medical chart folder'}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-purple-600" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      data-testid="client-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      data-testid="client-last-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-purple-600" />
                  Address
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                      <input
                        type="text"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_relationship}
                      onChange={(e) => setFormData({...formData, emergency_contact_relationship: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g., Spouse, Child, Friend"
                    />
                  </div>
                </div>
              </div>

              {/* Medical Info */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-purple-600" />
                  Medical Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Diagnosis</label>
                    <input
                      type="text"
                      value={formData.primary_diagnosis}
                      onChange={(e) => setFormData({...formData, primary_diagnosis: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Diagnoses</label>
                    <input
                      type="text"
                      value={formData.secondary_diagnoses}
                      onChange={(e) => setFormData({...formData, secondary_diagnoses: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Separate with commas"
                    />
                  </div>
                </div>
              </div>

              {/* Insurance */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  Insurance Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Provider</label>
                    <input
                      type="text"
                      value={formData.insurance_provider}
                      onChange={(e) => setFormData({...formData, insurance_provider: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Insurance ID / Member #</label>
                    <input
                      type="text"
                      value={formData.insurance_id}
                      onChange={(e) => setFormData({...formData, insurance_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Any special instructions, preferences, or notes..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAddForm(false); setEditingClient(null); }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={processingId === 'new'}
                >
                  {processingId === 'new' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : editingClient ? (
                    <Edit2 className="w-4 h-4 mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  {editingClient ? 'Update Client' : 'Create Client'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <Card className="p-12 text-center border-purple-100">
          <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Clients Found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery ? 'No clients match your search' : 'Start by adding your first client'}
          </p>
          {!searchQuery && (
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Client
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredClients.map((client) => (
            <Card 
              key={client.id} 
              className={`border-2 transition-all ${
                expandedClient === client.id 
                  ? 'border-purple-300 shadow-lg' 
                  : 'border-purple-100 hover:border-purple-200'
              }`}
            >
              {/* Client Header */}
              <div 
                className="p-6 cursor-pointer"
                onClick={() => handleExpand(client.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-pink-100 rounded-full">
                      <Heart className="w-6 h-6 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {client.first_name} {client.last_name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        {client.city && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {client.city}, {client.state}
                          </span>
                        )}
                        {client.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {client.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${STATUS_COLORS[client.status] || STATUS_COLORS.active}`}>
                      {client.status || 'active'}
                    </span>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Folder className="w-4 h-4" />
                      Chart Created
                    </span>
                    {expandedClient === client.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedClient === client.id && (
                <div className="border-t border-purple-100 p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Client Details */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                        Client Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        {client.date_of_birth && (
                          <p><span className="text-gray-500">DOB:</span> {formatDate(client.date_of_birth)}</p>
                        )}
                        {client.address && (
                          <p><span className="text-gray-500">Address:</span> {client.address}</p>
                        )}
                        {client.email && (
                          <p><span className="text-gray-500">Email:</span> {client.email}</p>
                        )}
                        {(client.emergency_contact_name || client.primary_contact_name) && (
                          <p>
                            <span className="text-gray-500">Emergency Contact:</span>{' '}
                            {client.emergency_contact_name || client.primary_contact_name}
                            {(client.emergency_contact_phone || client.primary_contact_phone) && 
                              ` - ${client.emergency_contact_phone || client.primary_contact_phone}`}
                          </p>
                        )}
                        <p><span className="text-gray-500">Chart Folder:</span> <code className="bg-gray-200 px-1 rounded">{client.folder_path}</code></p>
                      </div>
                    </div>

                    {/* Assigned Employees */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        Assigned Caregivers
                      </h4>
                      
                      {loadingAssignments[client.id] ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading assignments...
                        </div>
                      ) : assignments[client.id]?.length > 0 ? (
                        <div className="space-y-2 mb-4">
                          {assignments[client.id].map((assignment) => (
                            <div key={assignment.id} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                              <div>
                                <p className="font-medium text-gray-900">{assignment.employee_name}</p>
                                <p className="text-xs text-gray-500">{assignment.employee_email}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnassign(client.id, assignment.employee_id)}
                                disabled={processingId === `unassign-${client.id}-${assignment.employee_id}`}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                {processingId === `unassign-${client.id}-${assignment.employee_id}` ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mb-4">No caregivers assigned yet</p>
                      )}

                      {/* Assign New Employee */}
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedEmployee[client.id] || ''}
                          onChange={(e) => setSelectedEmployee(prev => ({ ...prev, [client.id]: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                          data-testid={`assign-select-${client.id}`}
                        >
                          <option value="">Select employee to assign...</option>
                          {employees
                            .filter(e => !assignments[client.id]?.some(a => a.employee_id === e.id))
                            .map(emp => (
                              <option key={emp.id} value={emp.id}>
                                {emp.full_name || emp.email} ({emp.role})
                              </option>
                            ))}
                        </select>
                        <Button
                          onClick={() => handleAssign(client.id)}
                          disabled={!selectedEmployee[client.id] || processingId === `assign-${client.id}`}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid={`assign-btn-${client.id}`}
                        >
                          {processingId === `assign-${client.id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserPlus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                    <Button
                      onClick={() => navigate(`/emr/${client.id}`)}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid={`open-emr-${client.id}`}
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Open EMR / Medical Records
                    </Button>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => handleEdit(client)}
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Client
                      </Button>
                      {client.status !== 'inactive' && (
                        <Button
                          variant="outline"
                          onClick={() => handleDeactivate(client.id)}
                          disabled={processingId === client.id}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          {processingId === client.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <XCircle className="w-4 h-4 mr-2" />
                          )}
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
